import { useState, useEffect, useRef } from 'preact/hooks';
import { getLocale, normalizeLocaleCode, setLocale } from '../i18n';
import { getConfig } from '../config';
import { store } from '../store';

const LOCALES: { code: string; label: string; flag: string }[] = [
  { code: 'en', label: 'EN', flag: '🇬🇧' },
  { code: 'de', label: 'DE', flag: '🇩🇪' },
  { code: 'fr', label: 'FR', flag: '🇫🇷' },
  { code: 'pt', label: 'PT', flag: '🇵🇹' },
  { code: 'es', label: 'ES', flag: '🇪🇸' },
  { code: 'it', label: 'IT', flag: '🇮🇹' },
];

function getLocaleFromUrl(): string {
  if (typeof window === 'undefined') return 'en';
  const m = window.location.search.match(/[?&]lang=([^&]+)/i);
  return normalizeLocaleCode(m ? m[1] : getLocale());
}

function setLocaleCookie(locale: string): void {
  if (typeof document === 'undefined') return;
  const code = normalizeLocaleCode(locale);
  if (!code) return;
  document.cookie = `sl_lang=${encodeURIComponent(code)}; path=/; max-age=${365 * 86400}; samesite=lax`;
}

function setLocaleInUrl(locale: string): void {
  setLocaleCookie(locale);
  const url = new URL(window.location.href);
  url.searchParams.set('lang', locale);
  window.location.href = url.toString();
}

export function LocaleWidget() {
  const [current, setCurrent] = useState(getLocaleFromUrl());
  const [isOpen, setIsOpen] = useState(false);
  const [settings, setSettings] = useState<any>(store.getSettings());
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const fromUrl = getLocaleFromUrl();
    if (window.location.search.match(/[?&]lang=/i)) {
      setLocale(fromUrl);
      setLocaleCookie(fromUrl);
    }
    setCurrent(fromUrl);
  }, []);

  useEffect(() => store.subscribe(() => setSettings(store.getSettings())), []);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    if (!isOpen || !dropdownRef.current || typeof window === 'undefined') return;
    const raf = window.requestAnimationFrame(() => {
      const menu = dropdownRef.current?.querySelector('.dropdown-menu.show') as HTMLElement | null;
      if (!menu) return;
      const navScroller = menu.closest('.sl-nav') as HTMLElement | null;
      if (!navScroller) return;
      const navRect = navScroller.getBoundingClientRect();
      const menuRect = menu.getBoundingClientRect();
      const pad = 12;
      const bottomOverflow = menuRect.bottom - navRect.bottom + pad;
      if (bottomOverflow > 0) navScroller.scrollTop += bottomOverflow;
    });
    return () => window.cancelAnimationFrame(raf);
  }, [isOpen]);

  const configuredFromSettings = Array.isArray(settings?.AVAILABLE_LANGUAGES) ? settings.AVAILABLE_LANGUAGES : [];
  const configuredFromConfig = Array.isArray(getConfig().availableLanguages) ? getConfig().availableLanguages : [];
  const configured = (configuredFromSettings.length ? configuredFromSettings : configuredFromConfig)
    .map((v: unknown) => normalizeLocaleCode(String(v ?? '')))
    .filter((v: string, i: number, arr: string[]) => v !== '' && arr.indexOf(v) === i);
  const locales = configured.length > 0
    ? LOCALES.filter((loc) => configured.includes(loc.code))
    : LOCALES;
  const currentLocale = locales.find((l) => l.code === current) ?? LOCALES.find((l) => l.code === current);
  const currentFlag = currentLocale?.flag ?? '';
  const currentLabelText = currentLocale ? currentLocale.label : current.toUpperCase();
  return (
    <div class="dropdown notranslate" ref={dropdownRef}>
      <a
        class="nav-link dropdown-toggle d-flex align-items-center gap-1"
        href="#"
        role="button"
        aria-expanded={isOpen}
        style={{ color: 'var(--sw-header-links, var(--sw-primary))' }}
        onClick={(e) => {
          e.preventDefault();
          setIsOpen(!isOpen);
        }}
      >
        {currentFlag ? <span class="sw-locale-flag">{currentFlag}</span> : null}
        <span>{currentLabelText}</span>
      </a>
      <ul
        class={`dropdown-menu ${isOpen ? 'show' : ''}`}
      >
        {locales.map(({ code, label, flag }) => (
          <li key={code}>
            <a
              class={`dropdown-item d-flex align-items-center gap-2 ${code === current ? 'active' : ''}`}
              href="#"
              onClick={(e) => {
                e.preventDefault();
                setLocale(code);
                setLocaleInUrl(code);
                setIsOpen(false);
              }}
            >
              <span class="sw-locale-flag">{flag}</span>
              <span>{label}</span>
            </a>
          </li>
        ))}
      </ul>
    </div>
  );
}
